import { type ComponentType, useEffect, useMemo, useState } from "react";
import { Button, Typography } from "@mui/material";
import { useMediaQuery } from "react-responsive";
import CreativeChannels from "./CreativeChannels";
import CreativeOptions from "./CreativeOptions";
import CreativeChannelsModal from "@/components/modals/CreativeChannelsModal";
// import { GlobalContext } from "@/helpers/contexts/contexts";
import AdvanceSettingForm from "@/features/campaigns/shared/AdvanceSettingForm";
import RpMUIDialog from "@/components/modals/RpMUIModal";

type CreativeId = string | number;

type Creative = {
  creative_id: CreativeId;
  creative_name: string;
};

type Campaign = {
  campaign_advanced_setting?: {
    is_required?: boolean;
  };
  creative_select_flag?: boolean;
  creative_select_max_value?: number;
  [key: string]: unknown;
};

type Location = {
  radius?: number;
  [key: string]: unknown;
};

type SettingsData = {
  settings?: unknown[];
};

type CreativeOption = {
  label: string;
  value: CreativeId;
};

type CreativeOptionsProps = {
  creativesOptions: CreativeOption[];
  initiallySelected: CreativeId[];
  onClickView: (_value: CreativeId) => void;
  onSelect: (_selectedCreatives: CreativeId[]) => void;
  currentInView: CreativeId;
  isTabletOrMobile: boolean;
  creative_select_flag?: boolean;
  creative_select_max_value?: number;
  initialDisabled?: boolean;
};

type CreativeChannelsProps = {
  loc: Location;
  view: CreativeId;
  creatives: Creative[];
  campaign?: Campaign;
};

type AdvanceSettingFormProps = {
  settingsData?: unknown[];
  onClose: () => void;
  onFillSettingsData?: (..._args: unknown[]) => void;
  productList?: unknown[];
  initialAnswers?: Record<string, unknown>;
  locationName: string;
  locationRadius?: number;
};

type CreativesProps = {
  campaign?: Campaign;
  creatives?: Creative[];
  initiallySelected?: CreativeId[];
  loc: Location;
  locationName?: string;
  onSelectCreative: (_selectedCreatives: CreativeId[]) => void;
  getAdvanceSettingData?: SettingsData;
  onFillSettingsData?: (..._args: unknown[]) => void;
  initialAnswers?: Record<string, unknown>;
  productList?: unknown[];
  initialDisabled?: boolean;
};

const TypedCreativeOptions =
  CreativeOptions as ComponentType<CreativeOptionsProps>;
const TypedCreativeChannels =
  CreativeChannels as ComponentType<CreativeChannelsProps>;
const TypedAdvanceSettingForm =
  AdvanceSettingForm as ComponentType<AdvanceSettingFormProps>;

export default function Creatives({
  campaign,
  creatives = [],
  initiallySelected = [],
  loc,
  locationName = "",
  onSelectCreative,
  getAdvanceSettingData,
  onFillSettingsData,
  initialAnswers,
  productList,
  initialDisabled,
}: CreativesProps) {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 766px)" });

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<CreativeId>(() => {
    if (initiallySelected && initiallySelected.length) {
      return initiallySelected[0];
    }
    return creatives && creatives.length ? creatives[0].creative_id : 0;
  });

  const [openAdvancedSetting, setAdvancedSetting] = useState(false);

  const settingsData = getAdvanceSettingData?.settings;

  const handleCloseModal = () => {
    setAdvancedSetting(false);
  };

  useEffect(() => {
    if (!isTabletOrMobile) {
      setOpen(false);
    }
  }, [isTabletOrMobile]);

  const toViewCreativeName = useMemo(() => {
    const creative = creatives.find((c) => c.creative_id === view);
    return creative ? creative.creative_name : "";
  }, [view, creatives]);

  const getCreativeOptions = useMemo<CreativeOption[]>(() => {
    return creatives.map((c) => {
      return {
        label: c.creative_name,
        value: c.creative_id,
      };
    });
  }, [creatives]);

  const handleOnClickView = (v: CreativeId) => {
    setView(v);
    if (isTabletOrMobile) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleOnSelect = (selectedCreatives: CreativeId[]) => {
    onSelectCreative(selectedCreatives);
  };

  // const creativeName = useMemo(() => {
  //   const creative = creatives.find((c) => c.creative_id === view);
  //   return creative ? creative.creative_name : "";
  // }, [creatives, view]);

  const renderAdvancedSetting = () => {
    if (!getAdvanceSettingData) {
      return null;
    }

    if (campaign?.campaign_advanced_setting?.is_required) {
      return null;
    }

    return (
      <div className="row">
        <div className="col-12">
          <Button
            color="primary"
            size="small"
            onClick={() => {
              setAdvancedSetting(true);
            }}
            className="mt-2"
          >
            Advanced Settings
          </Button>
        </div>
        <RpMUIDialog
          open={openAdvancedSetting}
          onClose={handleCloseModal}
          maxWidth="md"
        >
          <TypedAdvanceSettingForm
            settingsData={settingsData}
            onClose={handleCloseModal}
            onFillSettingsData={onFillSettingsData}
            productList={productList}
            initialAnswers={initialAnswers}
            locationName={locationName}
            locationRadius={loc.radius}
          />
        </RpMUIDialog>
      </div>
    );
  };

  return (
    <div className="creatives">
      <div className="card border-0">
        <div className="card-body p-0">
          <div className="row">
            <div className="col-md-5 col-lg-4 col-xl-3 pr-md-0 d-flex flex-column justify-content-between">
              <div>
                <Typography
                  component="h6"
                  variant="body2"
                  gutterBottom
                  className="fw-sb"
                >
                  Select One
                </Typography>
                <TypedCreativeOptions
                  creativesOptions={getCreativeOptions}
                  initiallySelected={initiallySelected}
                  onClickView={handleOnClickView}
                  onSelect={handleOnSelect}
                  currentInView={view}
                  isTabletOrMobile={isTabletOrMobile}
                  creative_select_flag={campaign?.creative_select_flag}
                  creative_select_max_value={
                    campaign?.creative_select_max_value
                  }
                  initialDisabled={initialDisabled}
                />
              </div>
              {settingsData?.length ? <>{renderAdvancedSetting()}</> : null}
            </div>
            <div className="col-md-7 col-lg-8 col-xl-9 pl-md-0 d-none d-md-block">
              <TypedCreativeChannels
                loc={loc}
                view={view}
                creatives={creatives}
                campaign={campaign}
              />
            </div>
          </div>
        </div>
      </div>
      {open && (
        <CreativeChannelsModal
          open={open}
          onClose={() => setOpen(false)}
          locationName={locationName}
          creativeName={toViewCreativeName}
        >
          <div className="px-sm-3">
            <TypedCreativeChannels
              loc={loc}
              view={view}
              creatives={creatives}
              campaign={campaign}
            />
          </div>
        </CreativeChannelsModal>
      )}
    </div>
  );
}
